import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { NodeLayout } from "@/components/organisms/Node/NodeLayout";

import { SettingsViewLayout } from "./SettingsViewLayout";
import { useSettingsEntity } from "./useSettingsEntity";
import { useSettingsEntityNode } from "./useSettingsEntityNode";

function useSettingsEntityGroupId(): string {
  const { pathname } = useLocation();

  return useMemo(() => {
    if (pathname.endsWith("/members")) return "members";
    if (pathname.endsWith("/roles")) return "roles";
    if (pathname.endsWith("/clients")) return "clients";
    if (pathname.endsWith("/billing")) return "billing";
    if (pathname.endsWith("/integrations")) return "integrations";
    if (pathname.endsWith("/templates")) return "templates";
    return "overview";
  }, [pathname]);
}

export function SettingsEntityView() {
  const { entity, isLoading: isLoadingEntity } = useSettingsEntity();
  const {
    store,
    isLoading: isLoadingNode,
    actions,
    metadata,
  } = useSettingsEntityNode();
  // Drawn loading while the person's businesses are read, then the business's
  // own record.
  const isLoading = isLoadingEntity || isLoadingNode;
  // The settings sidebar is the navigation: the route picks one group and the
  // page shows only that group, with no tab bar of its own. General is split:
  // the business's details beside its own records.
  const groupId = useSettingsEntityGroupId();
  const isGeneral = groupId === "overview";
  const visibleGroupIds = useMemo(
    () => (isGeneral ? ["overview", "documents"] : [groupId]),
    [groupId, isGeneral],
  );

  if (!entity && !isLoadingEntity) {
    return null;
  }

  return (
    <SettingsViewLayout
      store={store}
      title={metadata.title}
      isLoading={isLoading}
      actions={actions}
    >
      <NodeLayout
        key={groupId}
        autofocus={true}
        store={store}
        isLoading={isLoading}
        isSingleClick={true}
        forceLayoutMode={isGeneral ? "cards" : "tabs"}
        showTabs={false}
        initialGroupId={groupId}
        visibleGroupIds={visibleGroupIds}
      />
    </SettingsViewLayout>
  );
}
